import React from "react";
import type { UseFormRegister, FieldError } from "react-hook-form";

interface TextInputProps {
  id: string;
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  register?: UseFormRegister<any>;
  error?: FieldError;
  validation?: object;
  value?: string;
  min?: string;
  max?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  ariaDescribedBy?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

const MAX_INLINE_LABEL_LENGTH = 200;

const TextInput: React.FC<TextInputProps> = ({
  id,
  name,
  label,
  type = "text",
  placeholder = "",
  required = false,
  className = "gcw:w-full",
  labelClassName = "",
  inputClassName = "container-inputs",
  register,
  error,
  validation = {},
  value,
  min,
  max,
  onChange,
  onKeyDown,
  onBlur: customOnBlur,
  onFocus: customOnFocus,
  ariaDescribedBy,
  disabled,
  readOnly,
}) => {
  const truncatedPlaceholder = placeholder.length > MAX_INLINE_LABEL_LENGTH ? `` : placeholder;
  const placeholderWithRequired = required ? `${truncatedPlaceholder} *` : truncatedPlaceholder;
  const labelLongClass = label.length > MAX_INLINE_LABEL_LENGTH ? "long-label" : "";

  return (
    <div className={`${className} ${labelLongClass}`}>
      <label className={`gcw:block gcw:mb-2 ${labelClassName}`} htmlFor={id}>
        {label}
        {required && <span className="gcw:text-text-primary"> * </span>}
      </label>
      {register ? (
        <input
          type={type}
          className={`${inputClassName} ${error ? "gcw:border-error" : ""}`}
          id={id}
          placeholder={placeholderWithRequired}
          min={min}
          max={max}
          disabled={disabled}
          readOnly={readOnly}
          aria-describedby={[error ? `${id}-error` : undefined, ariaDescribedBy].filter(Boolean).join(" ") || undefined}
          {...((): React.InputHTMLAttributes<HTMLInputElement> => {
            const { onBlur: registerOnBlur, onChange: registerOnChange, ...rest } = register(name, validation);
            return {
              ...rest,
              onBlur: (e) => {
                registerOnBlur(e);
                customOnBlur?.();
              },
              onChange: (e) => {
                registerOnChange(e);
                onChange?.(e);
              },
              onFocus: customOnFocus,
            };
          })()}
        />
      ) : (
        <input
          type={type}
          className={`${inputClassName} ${error ? "gcw:border-error" : ""}`}
          id={id}
          placeholder={placeholderWithRequired}
          value={value}
          min={min}
          max={max}
          disabled={disabled}
          readOnly={readOnly}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={customOnFocus}
          aria-describedby={[error ? `${id}-error` : undefined, ariaDescribedBy].filter(Boolean).join(" ") || undefined}
        />
      )}
      {error && (
        <div id={`${id}-error`} className="gcw:text-error gcw:text-md gcw:mt-1" role="alert">
          {error.message}
        </div>
      )}
    </div>
  );
};

export default TextInput;
